import { Button, Group, Text, Textarea } from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { WeeklyNoteSaved } from "@msc/shared";

import { ApiError } from "../../lib/api.js";

const PAUSE_MS = 1500;

type Status = "idle" | "saving" | "saved" | "conflict" | "closed" | "error";

/**
 * Freitext mit Autosave (Fokusverlust oder Tipp-Pause). Die Version kommt
 * vom Server; ist sie veraltet, antwortet er 409 und wir überschreiben nichts.
 *
 * Timer, Blur und laufende Saves greifen auf denselben Stand zu — deshalb
 * liegen Text, Serverstand und Status zusätzlich in Refs: Ein Timer, der in
 * einem alten Render angelegt wurde, sähe sonst eine veraltete Version und
 * liefe in einen falschen 409. Es läuft immer höchstens ein Save; was während
 * eines laufenden Saves getippt wurde, wird danach nachgespeichert.
 */
export function NoteField({
  label,
  value,
  version,
  readOnly,
  save,
  onConflict,
}: {
  label?: string;
  value: string;
  version: number;
  readOnly: boolean;
  save: (vars: { notes: string; version: number }) => Promise<WeeklyNoteSaved>;
  onConflict: () => void;
}) {
  const { t } = useTranslation();
  const [text, setTextState] = useState(value);
  const [status, setStatusState] = useState<Status>("idle");

  const textRef = useRef(value);
  const standRef = useRef<{ text: string; version: number }>({ text: value, version });
  const statusRef = useRef<Status>("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  // Nach "Neu laden": den nächsten Serverstand übernehmen, auch wenn lokal
  // noch ein (abgelehnter) Text steht.
  const pendingReset = useRef(false);
  const saveRef = useRef(save);
  saveRef.current = save;

  const setText = (next: string) => {
    textRef.current = next;
    setTextState(next);
  };
  const setStatus = (next: Status) => {
    statusRef.current = next;
    if (mounted.current) setStatusState(next);
  };

  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  const uebernehmen = (serverText: string, serverVersion: number) => {
    setText(serverText);
    standRef.current = { text: serverText, version: serverVersion };
  };

  // Serverstand übernehmen, wenn er sich ändert und lokal nichts offen ist —
  // oder wenn der Nutzer nach einem Konflikt "Neu laden" gewählt hat.
  useEffect(() => {
    if (pendingReset.current || textRef.current === standRef.current.text) {
      pendingReset.current = false;
      uebernehmen(value, version);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, version]);

  const speichern = async (): Promise<void> => {
    clearTimer();
    // Der laufende Save speichert am Ende den neuesten Text nach.
    if (inFlight.current) return;
    if (statusRef.current === "conflict" || statusRef.current === "closed") return;
    const aktuell = textRef.current;
    if (aktuell === standRef.current.text) return;

    inFlight.current = true;
    setStatus("saving");
    let ok = false;
    try {
      const r = await saveRef.current({ notes: aktuell, version: standRef.current.version });
      standRef.current = { text: r.notes, version: r.version };
      setStatus("saved");
      ok = true;
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setStatus(err.code === "WEEKLY_CLOSED" ? "closed" : "conflict");
      } else {
        setStatus("error");
      }
    } finally {
      inFlight.current = false;
    }

    // Während des Saves weitergetippt (und kein Timer mehr offen, z. B. weil
    // inzwischen Blur kam): den neuesten Text einmal nachspeichern.
    if (ok && !timer.current && textRef.current !== standRef.current.text) {
      await speichern();
    }
  };

  const onChange = (next: string) => {
    setText(next);
    clearTimer();
    timer.current = setTimeout(() => {
      timer.current = null;
      void speichern();
    }, PAUSE_MS);
  };

  const neuLaden = () => {
    setStatus("idle");
    // Liegt bereits ein neuerer Stand in den Props, sofort übernehmen; sonst
    // übernimmt der Effekt den Stand, den der Refetch liefert.
    if (version !== standRef.current.version) {
      uebernehmen(value, version);
    } else {
      pendingReset.current = true;
    }
    onConflict();
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      // Ausstehende Änderung nicht verwerfen: best effort noch absenden.
      if (timer.current) void speichern();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadButton = (
    <Button size="compact-xs" variant="light" onClick={neuLaden}>
      {t("weekly.reload")}
    </Button>
  );

  return (
    <div>
      <Textarea
        label={label}
        autosize
        minRows={2}
        value={text}
        readOnly={readOnly}
        placeholder={readOnly ? undefined : t("weekly.notesPlaceholder")}
        onChange={(e) => onChange(e.currentTarget.value)}
        onBlur={() => void speichern()}
      />
      <Group gap="xs" mt={4} h={20}>
        {status === "saving" && (
          <Text size="xs" c="dimmed">
            {t("weekly.saving")}
          </Text>
        )}
        {status === "saved" && (
          <Text size="xs" c="dimmed">
            {t("weekly.saved")}
          </Text>
        )}
        {status === "error" && (
          <Text size="xs" c="red">
            {t("weekly.saveError")}
          </Text>
        )}
        {status === "conflict" && (
          <>
            <Text size="xs" c="red">
              {t("weekly.conflict")}
            </Text>
            {reloadButton}
          </>
        )}
        {status === "closed" && (
          <>
            <Text size="xs" c="red">
              {t("weekly.closedElsewhere")}
            </Text>
            {reloadButton}
          </>
        )}
      </Group>
    </div>
  );
}
